<template>
    <div class="label-confirmation-input">
        <CompassCheckbox
            :modelValue="showConditions"
            :checked="showConditions"
            :label="$t.__('settings.sellsymodule.invoicing.conditions.confirmation_checkbox_label')"
            @change="showConditions = $event"
        />

        <Transition name="slide">
            <CompassTabs v-show="showConditions" v-model="currentTab">
                <CompassTabPane
                    v-for="lang in uiLangs"
                    :key="lang.id"
                    :label="lang.label"
                    :name="lang.id"
                >
                    <CompassFormItem
                        :label="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.confirmation_label_form_item_label',
                            )
                        "
                        :info="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.confirmation_label_caption',
                            )
                        "
                        :error="lang.id === defaultUiLang ? labelError : ''"
                        :required="lang.id === defaultUiLang"
                        :tooltip="
                            hadConditionFile
                                ? $t.__('settings.sellsymodule.invoicing.conditions.tooltip_label')
                                : ''
                        "
                        class="label-confirmation-input__text-input"
                    >
                        <CompassInput
                            :modelValue="getConditionLabel(lang.id)"
                            :placeholder="
                                $t.__(
                                    'settings.sellsymodule.invoicing.conditions.confirmation_label_placeholder',
                                )
                            "
                            :error="lang.id === defaultUiLang && !!labelError"
                            @update:modelValue="updateConditionLabel(lang.id, $event)"
                        />
                    </CompassFormItem>
                    <CompassFormItem
                        v-if="hadConditionFile"
                        :label="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.search_file_input_label',
                            )
                        "
                        :info="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.search_file_input_caption',
                            )
                        "
                        class="label-confirmation-input__file-search-input"
                    >
                        <CompassAutoComplete
                            :results="fileSearchResults"
                            :loading="fileSearchLoading"
                            :placeholder="
                                $t.__(
                                    'settings.sellsymodule.invoicing.conditions.search_file_input_placeholder',
                                )
                            "
                            :noResultsLabel="$t.__('common.labels.no_result')"
                            :defaultValues="getDefaultFileValue(lang.id)"
                            :menuClass="getAutocompleteDropdownClasses(lang.id)"
                            @updateSearch="searchFiles($event)"
                            @update:modelValue="updateConditionFile(lang.id, $event)"
                        />
                    </CompassFormItem>
                </CompassTabPane>
            </CompassTabs>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import { useStore } from 'vuex';
import type { UILang } from 'types/api/settings/invoicing/invoicingSettingsTypes';
import type { ConditionTranslation } from 'types/api/settings/invoicing/invoicingConditionsTypes';
import type { FileItem } from 'types/api/fileTypes';
import type { Translator } from 'types/injects/translator.type';
import { searchFiles as searchFilesApi } from 'api/file/fileService';
import { captureException } from '@sentry/vue';
import { useFile } from 'composables/useFile';
import { debounce } from 'lodash';

interface Props {
    uiLangs: UILang[];
    hadConditionFile: boolean;
}

defineProps<Props>();

const conditions = defineModel<ConditionTranslation[]>({
    required: true,
});
const showConditions = defineModel<boolean>('showConditions', { default: false });

const $t = inject('$t') as Translator;
const store = useStore();

interface AutoCompleteOption {
    label: string;
    value: number;
}

//le model conditions aura toujours au moins un item venant de l'api, car il se base sur les langues de l'interface
const currentTab = ref(conditions.value.at(0)!.lang);
const fileSearchResults = ref<AutoCompleteOption[]>([]);
const fileSearchLoading = ref(false);
const labelError = ref<string>('');

const defaultUiLang = computed<string>(() => store.getters['corp/getUILang']);
const { getFileName, getFileSize } = useFile();

const getAutocompleteDropdownClasses = (langId: string): string => {
    return currentTab.value !== langId
        ? 'label-confirmation-input__file-search-input-dropdown--hidden'
        : '';
};

const getConditionLabel = (langId: string): string => {
    const condition = conditions.value.find((conditionItem) => conditionItem.lang === langId);
    return condition?.label ?? '';
};

const getDefaultFileValue = (langId: string): AutoCompleteOption[] => {
    const condition = conditions.value.find((conditionItem) => conditionItem.lang === langId);
    if (!condition?.file) {
        return [];
    }
    return [{ label: `${condition.file.name}.pdf`, value: condition.file.id }];
};

const searchFiles = debounce(async (term: string): Promise<void> => {
    fileSearchLoading.value = true;
    try {
        //on filtre par "attachement" et non "attachment" car c'est ce qui est attendu par l'api et que le changer serait un breaking change
        const { data: results } = (await searchFilesApi(term, ['attachement'], 'pdf')) as {
            data: FileItem[];
        };
        fileSearchResults.value = results.map((file: FileItem) => ({
            label: getFileName(file),
            value: file.id,
            caption: getFileSize(file.size),
        }));
    } catch (e) {
        captureException(e);
        fileSearchResults.value = [];
    } finally {
        fileSearchLoading.value = false;
    }
}, 200);

const updateConditionLabel = (langId: string, value: string): void => {
    const condition = conditions.value.find((condition) => condition.lang === langId);
    if (condition) {
        condition.label = value;
    }
    if (langId === defaultUiLang.value && value.trim() !== '') {
        labelError.value = '';
    }
};

const updateConditionFile = (langId: string, selected: AutoCompleteOption): void => {
    const condition = conditions.value.find((condition) => condition.lang === langId);
    if (condition) {
        condition.file = selected ? { id: selected.value, name: selected.label } : null;
    }
};

const validate = (): boolean => {
    if (!showConditions.value) {
        labelError.value = '';
        return true;
    }
    const defaultCondition = conditions.value.find(
        (condition) => condition.lang === defaultUiLang.value,
    );
    if (!defaultCondition || defaultCondition.label.trim() === '') {
        labelError.value = $t.__(
            'settings.sellsymodule.invoicing.conditions.confirmation_label_required',
        );
        currentTab.value = defaultUiLang.value;
        return false;
    }
    labelError.value = '';
    return true;
};

watch(showConditions, (value) => {
    if (!value) {
        labelError.value = '';
    }
});

defineExpose({ validate });
</script>

<style lang="scss" scoped>
.label-confirmation-input {
    .compass-checkbox {
        margin-bottom: var(--spacing-3);
    }
    .slide-enter-active,
    .slide-leave-active {
        transition: all 0.2s ease-in-out;
    }
    .slide-enter-from,
    .slide-leave-to {
        transform: translateY(-10px);
        opacity: 0;
    }
    :deep(.compass-tab-pane) {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: var(--spacing-3);
        .label-confirmation-input__text-input {
            grid-column: span 12;

            &:has(+ .label-confirmation-input__file-search-input) {
                grid-column: span 7;
            }
        }
        .label-confirmation-input__file-search-input {
            grid-column: span 5;
        }
    }
}
</style>

<style lang="scss">
#compass-dropdown-container {
    .label-confirmation-input__file-search-input-dropdown--hidden {
        display: none;
    }
}
</style>
